import React, { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Activity } from '../../types'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { NumberTracker } from '../../../../../interaxDesignSystemComponents'
import { SortableActivityCard } from '../ActivityCard'

export interface ConfigureActivitiesSectionProps {
  selectedActivities: Activity[]
  isCustomPurpose: boolean
  onReorderActivities: (activities: Activity[]) => void
  onRemoveActivity: (activityId: string) => void
  renderActivityDetails?: (activity: Activity) => React.ReactNode
  /** Optional control rendered inline next to each activity's duration (e.g. an edit toggle). */
  renderDurationAction?: (activity: Activity) => React.ReactNode
  className?: string
}

export const ConfigureActivitiesSection: React.FC<ConfigureActivitiesSectionProps> = ({
  selectedActivities,
  isCustomPurpose,
  onReorderActivities,
  onRemoveActivity,
  renderActivityDetails,
  renderDurationAction,
  className = ''
}) => {
  const { t } = useTranslation()
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [itemHeights, setItemHeights] = useState<number[]>([])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || !isCustomPurpose) return

    const activeIndex = selectedActivities.findIndex((activity) => activity.id === active.id)
    const overIndex = selectedActivities.findIndex((activity) => activity.id === over.id)

    if (activeIndex !== overIndex) {
      const reorderedActivities = arrayMove(selectedActivities, activeIndex, overIndex)
      onReorderActivities(reorderedActivities)
    }
  }

  const handleActivityRemove = (activityId: string) => {
    onRemoveActivity(activityId)
  }

  useLayoutEffect(() => {
    const updateHeights = () => {
      setItemHeights(
        selectedActivities.map((activity) => itemRefs.current[activity.id]?.getBoundingClientRect().height ?? 80)
      )
    }

    updateHeights()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      updateHeights()
    })

    selectedActivities.forEach((activity) => {
      const node = itemRefs.current[activity.id]
      if (node) {
        observer.observe(node)
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [selectedActivities, renderActivityDetails])

  return (
    <div className={`space-y-6 ${className}`}>
      {selectedActivities.length === 0 ? (
        <div
          className="text-center py-8"
          style={{
            color: INTERAX_COLORS.typographyFont,
            fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body2.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body2.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body2.lineHeight
          }}
        >
          {t('facilitator.trainingSprint.contentStep.emptyState')}
        </div>
      ) : (
        <div className="flex gap-6">
          <NumberTracker items={selectedActivities} itemSpacing={14} itemHeights={itemHeights} visible={true} />
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={selectedActivities.map((activity) => activity.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex-1 space-y-3">
                {selectedActivities.map((activity) => (
                  <div
                    key={activity.id}
                    ref={(node) => {
                      itemRefs.current[activity.id] = node
                    }}
                  >
                    <SortableActivityCard
                      activity={activity}
                      onRemove={isCustomPurpose ? handleActivityRemove : undefined}
                      isDraggable={isCustomPurpose}
                      details={renderActivityDetails?.(activity)}
                      durationEditSlot={renderDurationAction?.(activity)}
                    />
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  )
}
